import React, { useState, useEffect, useRef, useCallback } from 'react';
import classNames from 'classnames';

import { Toast } from 'primereact/toast';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Dropdown } from 'primereact/dropdown';
import { Dialog } from 'primereact/dialog';
import { Tag } from 'primereact/tag';
import { Card } from 'primereact/card';
import { Toolbar } from 'primereact/toolbar';
import { FilterMatchMode } from 'primereact/api';
import { ProgressSpinner } from 'primereact/progressspinner';
import DialogHeader from '../components/ui/DialogHeader';
import PageHeader from '../components/ui/PageHeader';

import { useAuth } from '../context/AuthContext';

export const AdminLocales = () => {
    const { axiosInstance } = useAuth();
    const toast = useRef(null);
    const dt = useRef(null);

    // Estados de datos
    const [locales, setLocales] = useState([]);
    const [responsables, setResponsables] = useState([]);
    const [loading, setLoading] = useState(false);

    // Estados del Modal / Formulario
    const emptyLocal = {
        id: null,
        nombre: '',
        direccion: '',
        cantidadMesas: null,
        electoresHabiles: null,
        responsableId: null
    };

    const colors = {
        primary: '#0B396E',
        accent: '#800000',
        text: '#444444',
    };

    const [localForm, setLocalForm] = useState(emptyLocal);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [saving, setSaving] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);

    // Filtro global de búsqueda
    const [filters, setFilters] = useState({
        global: { value: null, matchMode: FilterMatchMode.CONTAINS }
    });
    const [globalFilterValue, setGlobalFilterValue] = useState('');

    // Petición para listar todos los locales
    const fetchLocales = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.get('/locales');
            setLocales(res.data);
        } catch (err) {
            console.error("Error al cargar locales:", err);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'No se pudieron recuperar los locales de votación.',
                life: 3000
            });
        } finally {
            setLoading(false);
        }
    }, [axiosInstance]);

    // Petición para listar responsables elegibles
    const fetchResponsables = useCallback(async () => {
        try {
            const res = await axiosInstance.get('/locales/responsables-disponibles');
            setResponsables(res.data);
            return res.data;
        } catch (err) {
            console.error("Error al cargar responsables:", err);
            return [];
        }
    }, [axiosInstance]);

    useEffect(() => {
        fetchLocales();
        fetchResponsables();
    }, [fetchLocales, fetchResponsables]);

    // Manejo de búsqueda
    const onGlobalFilterChange = (e) => {
        const val = e.target.value;
        let _filters = { ...filters };
        _filters['global'].value = val;
        setFilters(_filters);
        setGlobalFilterValue(val);
    };

    // Apertura y Cierre de Modal
    const openNew = async () => {
        setLocalForm(emptyLocal);
        setIsEditMode(false);
        setSubmitted(false);
        await fetchResponsables();
        setDialogOpen(true);
    };

    const editLocal = async (localData) => {
        setIsEditMode(true);
        setSubmitted(false);

        const listaDisponibles = await fetchResponsables();

        // Si el responsable actual no está en la lista de disponibles, lo agregamos para que aparezca seleccionado
        if (localData.responsableId && !listaDisponibles.some(r => r.id === localData.responsableId)) {
            const responsableActual = {
                id: localData.responsableId,
                nombreCompleto: localData.responsableNombreCompleto,
                username: ''
            };
            setResponsables([responsableActual, ...listaDisponibles]);
        }

        setLocalForm({
            id: localData.id,
            nombre: localData.nombre || '',
            direccion: localData.direccion || '',
            cantidadMesas: localData.cantidadMesas,
            electoresHabiles: localData.electoresHabiles,
            responsableId: localData.responsableId
        });

        setDialogOpen(true);
    };

    const hideDialog = () => {
        setSubmitted(false);
        setDialogOpen(false);
    };

    // Guardar nuevo Local
    const guardarLocal = async () => {
        setSubmitted(true);

        if (!localForm.nombre.trim() || !localForm.direccion.trim() || !localForm.cantidadMesas || !localForm.electoresHabiles || !localForm.responsableId) {
            toast.current?.show({
                severity: 'warn',
                summary: 'Atención',
                detail: 'Por favor complete todos los campos obligatorios.',
                life: 3000
            });
            return;
        }

        setSaving(true);
        try {
            const payload = {
                nombre: localForm.nombre.trim(),
                direccion: localForm.direccion.trim(),
                cantidadMesas: localForm.cantidadMesas,
                electoresHabiles: localForm.electoresHabiles,
                responsableId: localForm.responsableId
            };

            if (isEditMode) {
                await axiosInstance.put(`/locales/${localForm.id}`, payload);
                toast.current?.show({
                    severity: 'success',
                    summary: 'Actualizado',
                    detail: `Local "${payload.nombre}" actualizado correctamente.`,
                    life: 3500
                });
            } else {
                await axiosInstance.post('/locales', payload);
                toast.current?.show({
                    severity: 'success',
                    summary: 'Registrado',
                    detail: `Local "${payload.nombre}" registrado exitosamente.`,
                    life: 3500
                });
            }

            hideDialog();
            fetchLocales();
        } catch (err) {
            console.error("Error al guardar local:", err);
            const msg = err.response?.data?.message || 'Ocurrió un error al guardar el local.';
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: msg,
                life: 5000
            });
        } finally {
            setSaving(false);
        }
    };

    // KPIs calculados
    const totalMesas = locales.reduce((acc, curr) => acc + (curr.cantidadMesas || 0), 0);
    const totalElectores = locales.reduce((acc, curr) => acc + (curr.electoresHabiles || 0), 0);

    // Plantillas de visualización en la tabla
    const headerToolbarLeft = () => (
        <div className="flex gap-2">
            <Button label="Nuevo Local" icon="pi pi-plus" style={{ backgroundColor: colors.accent, borderColor: colors.accent, color: 'white' }} onClick={openNew} />
            <Button label="Actualizar" icon="pi pi-refresh" className="p-button-text p-button-secondary" onClick={fetchLocales} />
        </div>
    );

    const tableHeader = (
        <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center gap-2">
            <span className="p-input-icon-left">
                <InputText
                    value={globalFilterValue}
                    onChange={onGlobalFilterChange}
                    placeholder="Buscar local, dirección o responsable..."
                    className="p-inputtext-sm w-full md:w-20rem"
                />
            </span>
        </div>
    );

    const dialogFooter = (
        <div className="flex justify-content-end gap-2">
            <Button label="Cancelar" icon="pi pi-times" className="p-button-text" onClick={hideDialog} disabled={saving} />
            <Button label={isEditMode ? "Actualizar Local" : "Guardar Local"} icon="pi pi-check" className="p-button-primary" onClick={guardarLocal} loading={saving} />
        </div>
    );

    const accionesBodyTemplate = (rowData) => {
        return (
            <div className="flex justify-content-center">
                <Button
                    icon="pi pi-pencil"
                    className="p-button-rounded p-button-outlined p-button-sm p-button-warning"
                    tooltip="Editar Local y Coordinador"
                    tooltipOptions={{ position: 'top' }}
                    onClick={() => editLocal(rowData)}
                />
            </div>
        );
    };

    return (
        <div className="grid">
            <Toast ref={toast} />

            {/* Fila de Tarjetas / KPIs */}
            <div className="col-12 md:col-4">
                <div className="card surface-card shadow-1 border-round p-3 flex justify-content-between align-items-center">
                    <div>
                        <span className="text-secondary text-sm font-semibold uppercase">Total de Locales</span>
                        <div className="text-900 font-bold text-3xl mt-1">{locales.length}</div>
                    </div>
                    <div className="border-circle bg-blue-100 flex align-items-center justify-content-center" style={{ width: '3rem', height: '3rem' }}>
                        <i className="pi pi-building text-blue-600 text-xl" />
                    </div>
                </div>
            </div>
            <div className="col-12 md:col-4">
                <div className="card surface-card shadow-1 border-round p-3 flex justify-content-between align-items-center">
                    <div>
                        <span className="text-secondary text-sm font-semibold uppercase">Mesas Habilitadas</span>
                        <div className="text-900 font-bold text-3xl mt-1">{totalMesas}</div>
                    </div>
                    <div className="border-circle bg-green-100 flex align-items-center justify-content-center" style={{ width: '3rem', height: '3rem' }}>
                        <i className="pi pi-table text-green-600 text-xl" />
                    </div>
                </div>
            </div>
            <div className="col-12 md:col-4">
                <div className="card surface-card shadow-1 border-round p-3 flex justify-content-between align-items-center">
                    <div>
                        <span className="text-secondary text-sm font-semibold uppercase">Electores Hábiles</span>
                        <div className="text-900 font-bold text-3xl mt-1">{totalElectores.toLocaleString()}</div>
                    </div>
                    <div className="border-circle bg-purple-100 flex align-items-center justify-content-center" style={{ width: '3rem', height: '3rem' }}>
                        <i className="pi pi-users text-purple-600 text-xl" />
                    </div>
                </div>
            </div>

            {/* Panel Principal con Toolbar y Tabla */}
            <div className="col-12">
                <PageHeader
                    title="Padrón de Locales Electorales"
                    description="Módulo de amntenimiento de Locales de Votación y Asignación de Responsables"
                    icon="pi pi-file"
                />
                <div className="card surface-card shadow-2 border-round p-4">
                    <Toolbar className="mb-3 border-none p-0" left={headerToolbarLeft} />

                    <DataTable
                        ref={dt}
                        value={locales}
                        loading={loading}
                        filters={filters}
                        globalFilterFields={['nombre', 'direccion', 'responsableNombreCompleto']}
                        header={tableHeader}
                        paginator
                        rows={10}
                        rowsPerPageOptions={[5, 10, 25]}
                        className="p-datatable-sm"
                        emptyMessage="No se encontraron locales de votación registrados."
                        responsiveLayout="scroll"
                    >
                        <Column field="id" header="ID" style={{ width: '70px' }} />
                        <Column field="nombre" header="Nombre del Local" style={{ minWidth: '14rem' }} />
                        <Column field="direccion" header="Dirección" style={{ minWidth: '16rem' }} />
                        <Column
                            field="cantidadMesas"
                            header="Mesas"
                            style={{ width: '100px', textAlign: 'center' }}
                            body={(d) => <Tag value={`${d.cantidadMesas} mesas`} severity="info" />}
                        />
                        <Column
                            field="electoresHabiles"
                            header="Electores"
                            style={{ width: '120px', textAlign: 'right' }}
                            body={(d) => d.electoresHabiles?.toLocaleString()}
                        />
                        <Column
                            field="responsableNombreCompleto"
                            header="Responsable Asignado"
                            sortable
                            style={{ minWidth: '14rem' }}
                            body={(d) => (
                                <span className="font-semibold text-primary">
                                    <i className="pi pi-user mr-2 text-secondary" />
                                    {d.responsableNombreCompleto || 'Sin Asignar'}
                                </span>
                            )}
                        />
                        <Column
                            header="Acciones"
                            body={accionesBodyTemplate}
                            style={{ width: '100px', textAlign: 'center' }}
                        />
                    </DataTable>
                </div>
            </div>

            {/* Modal de Creación de Local y Asignación de Responsable */}
            <Dialog
                visible={dialogOpen}
                style={{ width: '600px', maxWidth: '95vw' }}
                header={
                    <DialogHeader
                        title={<span style={{ color: 'white' }}>{isEditMode ? 'Modificar Local de Votación' : 'Registrar Nuevo Local'}</span>}
                        subtitle={<span style={{ color: 'white' }}>{isEditMode ? 'Actualice datos, mesas o asigne un nuevo coordinador' : 'Registre un nuevo local de votación en el sistema'}</span>}
                        icon={isEditMode ? "pi pi-pencil" : "pi pi-plus"}
                    />
                }
                modal
                className="p-fluid custom-profile-dialog"
                footer={dialogFooter}
                onHide={hideDialog}
            >
                <div className="p-fluid formgrid grid pt-2">
                    <div className="field col-12">
                        <label htmlFor="nombre">Nombre de la Institución o Local *</label>
                        <InputText
                            id="nombre"
                            value={localForm.nombre}
                            onChange={(e) => setLocalForm({ ...localForm, nombre: e.target.value })}
                            placeholder="Ej. I.E. Francisco Lizarzaburu"
                            className={classNames({ 'p-invalid': submitted && !localForm.nombre.trim() })}
                        />
                        {submitted && !localForm.nombre.trim() && <small className="p-error">El nombre es requerido.</small>}
                    </div>

                    <div className="field col-12">
                        <label htmlFor="direccion">Dirección / Ubicación *</label>
                        <InputText
                            id="direccion"
                            value={localForm.direccion}
                            onChange={(e) => setLocalForm({ ...localForm, direccion: e.target.value })}
                            placeholder="Ej. Av. Sánchez Carrión 450 - El Porvenir"
                            className={classNames({ 'p-invalid': submitted && !localForm.direccion.trim() })}
                        />
                        {submitted && !localForm.direccion.trim() && <small className="p-error">La dirección es requerida.</small>}
                    </div>

                    <div className="field col-12 md:col-6">
                        <label htmlFor="cantidadMesas">Cantidad de Mesas *</label>
                        <InputNumber
                            id="cantidadMesas"
                            value={localForm.cantidadMesas}
                            onValueChange={(e) => setLocalForm({ ...localForm, cantidadMesas: e.value })}
                            min={1}
                            placeholder="Ej. 12"

                            className={classNames({ 'p-invalid': submitted && !localForm.cantidadMesas })}
                        />
                        {submitted && !localForm.cantidadMesas && <small className="p-error">Indique el total de mesas.</small>}
                    </div>

                    <div className="field col-12 md:col-6">
                        <label htmlFor="electoresHabiles">Electores Hábiles *</label>
                        <InputNumber
                            id="electoresHabiles"
                            value={localForm.electoresHabiles}
                            onValueChange={(e) => setLocalForm({ ...localForm, electoresHabiles: e.value })}
                            min={1}
                            placeholder="Ej. 3600"
                            className={classNames({ 'p-invalid': submitted && !localForm.electoresHabiles })}
                        />
                        {submitted && !localForm.electoresHabiles && <small className="p-error">Indique electores hábiles.</small>}
                    </div>

                    <div className="field col-12">
                        <label htmlFor="responsableId">Responsable de Local (Usuario Registrado) *</label>
                        <Dropdown
                            id="responsableId"
                            value={localForm.responsableId}
                            options={responsables}
                            onChange={(e) => setLocalForm({ ...localForm, responsableId: e.value })}
                            optionLabel="nombreCompleto"
                            optionValue="id"
                            placeholder="Seleccione al responsable encargado"
                            filter
                            filterBy="nombreCompleto,username"
                            emptyFilterMessage="No se encontraron usuarios"
                            className={classNames({ 'p-invalid': submitted && !localForm.responsableId })}
                        />
                        {submitted && !localForm.responsableId && <small className="p-error">Debe asignar un responsable.</small>}
                    </div>
                </div>
            </Dialog>
        </div>
    );
};